<script setup>
import _ from 'lodash'
import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component'
import IGDBGameSelect from '@/components/IGDBGameSelect.vue'
import DropdownSource from '@/components/dropdowns/DropdownDiscoverSource.vue'
import { axios } from '@/utils/axios'
import MultiTagInput from '@/components/inputs/MultiTagInput.vue'

import {
    Dialog as DialogModal,
    DialogContent,
    DialogTitle,
    DialogHeader,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import Tooltip from '@/components/Tooltip.vue'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const store = useStore()

const props = defineProps({
    platformId: {
        type: [String, Array],
        required: false,
        default: null,
    },
    platformIds: {
        type: Array,
        required: false,
        default: null,
    },
    isLoading: {
        type: Boolean,
        required: false,
    },
    defaultListId: {
        type: String,
        required: false,
    },
    defaultTitle: {
        type: String,
        required: false,
    },
    defaultReportOptions : {
        type: Object,
        required: false
    },
    editReportId : {
        type: String,
        required: false,
    }
})

const emit = defineEmits(['filter-change', 'platform-change'])

const { checkPermission } = store.getters
const isListsEnabled = computed(() => checkPermission('module.lists'))
const isReportsEnabled = computed(() => checkPermission('module.reports'))
const isInstagramStoriesEnabled = computed(() => checkPermission('feature.instagram.stories'))
const listSources = ref(null)
const additionalContentOpen = ref(false)
const additionalContent = ref('')
const initialSelectedList = ref(null)
const filtersHaveChanged = ref(false)
const manualYouTubeChannelUrl = ref('')
const manualYouTubeChannelId = ref(null)

const IGDBSelectKey = ref(Date.now())

const defaultSponsorshipConfig = () => {
    return {
        changed : false,
        enabled : false,
        mustMatchGame : true,
        includeShoutouts : false,
        forceReanalyze : false,
    }
}

const sponsorshipConfig = ref(defaultSponsorshipConfig())

const platformValues = _.map(['instagram', 'tiktok', 'youtube', 'twitch', 'twitter'], (platformValueId) => {
    return {
        id: platformValueId,
        label: store.getters.getFromDictionary(`platforms.toProper.${platformValueId}`),
    }
})

const platforms = ref(platformValues)
const filtersDefault = {
    game: null,
    tags: [],
    taggedUserNames: [],
    startAt: moment().subtract(1, 'days').startOf('day').toDate(),
    endAt: moment().subtract(1, 'days').endOf('day').toDate(),
    listId : null,
    additionalContent : [],
    additionalContentOnly: false,
    isIgnoreTiny : true,
    title : '',
    contentType: '', // Default to "Any" (empty string for HTML select compatibility)
    hashtagGameFilterLogic: 'and', // 'and' or 'or' - how to combine hashtags and game filters
}

const filters = ref(_.cloneDeep(filtersDefault))
const detectedAdditionalContentPlatformIds = ref([])

const isLinksOnlyMode = computed(() => filters.value.additionalContentOnly === true)

const syncAdditionalContentDetails = (platformHints = null) => {
    const extractedContent = extractAdditionalContentDetails()
    const hintedPlatformIds = Array.isArray(platformHints)
        ? extractedContent.platformIds.filter(platformId => platformHints.includes(platformId))
        : []

    filters.value.additionalContent = extractedContent.ids
    detectedAdditionalContentPlatformIds.value = hintedPlatformIds.length > 0
        ? hintedPlatformIds
        : extractedContent.platformIds
}

const setLinksOnlyMode = (value) => {
    filters.value.additionalContentOnly = value

    if (value === true) {
        syncAdditionalContentDetails(selectedPlatformIds.value)
    }
}

// Computed property to convert taggedUserNames array to mention tags format
const mentionTags = computed(() => {
    return filters.value.taggedUserNames.map(username => ({ id: username }))
})

// Update mentions from MultiTagInput component
const updateMentions = (mentionItems) => {
    filters.value.taggedUserNames = mentionItems.map(item => item.id)
}

const pickerConfigBase = {
    enableTime: true,
    dateFormat: 'Z',
    altFormat: 'F j, Y h:i K',
    altInput: true,
    static: true,
}

const startPickerConfig = computed(() => ({
    ...pickerConfigBase,
    maxDate: filters.value.endAt ? moment(filters.value.endAt).toDate() : moment().toDate(),
}))

const endPickerConfig = computed(() => ({
    ...pickerConfigBase,
    minDate: filters.value.startAt ? moment(filters.value.startAt).toDate() : undefined,
    maxDate: moment().toDate(),
}))

// Initialize platform IDs from props - use a function to get initial value
const getInitialPlatformIds = () => {
    if (props.platformIds && Array.isArray(props.platformIds) && props.platformIds.length > 0) {
        return props.platformIds
    } else if (props.platformId) {
        // Handle both string and array for backward compatibility
        return Array.isArray(props.platformId) ? props.platformId : [props.platformId]
    }
    return []
}

const localPlatformId = ref(getInitialPlatformIds()[0] || null)
// Default to all platforms selected if no initial platform IDs provided
const selectedPlatformIds = ref(
    getInitialPlatformIds().length > 0
        ? [...getInitialPlatformIds()]
        : [...platforms.value.map(p => p.id)]
)

// Computed to get union of content types for selected platforms, with "Any" always first
const availableContentTypes = computed(() => {
    const allTypes = new Map()

    let platformsToMakeAvailable = selectedPlatformIds.value
    if (selectedPlatformIds.value?.length === 0) {
        platformsToMakeAvailable = ['none']
    }

    platformsToMakeAvailable.forEach(platformId => {
        const types = platformContentTypes[platformId] || []
        types.forEach(type => {
            if (type && type.id !== undefined) {
                // Use id as key to avoid duplicates (empty string for "Any")
                allTypes.set(type.id === null ? '' : type.id, type)
            }
        })
    })

    const typesArray = Array.from(allTypes.values())
    // Sort to ensure "Any" (empty string) is always first
    return typesArray.sort((a, b) => {
        if (a.id === '' || a.id === null) return -1
        if (b.id === '' || b.id === null) return 1
        return 0
    })
})

const platformContentTypes = {
    twitch: [
        { id: '', label: 'Any' }, // Use empty string for HTML select compatibility
        { id: 'stream', label: 'Stream', isDefault: true },
    ],
    youtube: [
        { id: '', label: 'Any' },
        { id: 'video', label: 'Video', isDefault: true },
        { id: 'short', label: 'Short' },
        { id: 'stream', label: 'Stream' },
    ],
    tiktok: [
        { id: '', label: 'Any' },
        { id: 'video', label: 'Short', isDefault: true },
    ],
    twitter: [
        { id: '', label: 'Any' },
        { id: 'post', label: 'Post', isDefault: true },
    ],
    instagram: [
        { id: '', label: 'Any' },
        { id: 'post', label: 'Post (Image + Carousel)', isDefault: true },
        { id: 'image', label: 'Photo' },
        { id: 'carousel', label: 'Carousel' },
        { id: 'video', label: 'Reel' },
        { id: 'story', label: 'Story' },
    ],
    none : [{ id: '', label: 'Any' }]
}
const defaultSelectedGame = ref(null)

// Track which game hashtags are enabled/disabled
const enabledGameHashtags = ref(new Set())

// Holds original options while copying so the game watcher can restore which hashtags were on/off
const pendingDefaultOptions = ref(null)

const nonGamePlatformIds = ['twitter', 'instagram', 'tiktok']
const gameFilteringPlatformIds = ['youtube', 'twitch']

// Helper function to convert game name to hashtag format
const gameNameToHashtag = (name) => {
    if (!name) return null
    // Remove accents, convert to lowercase, remove spaces and special characters
    return name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') // Remove non-alphanumeric characters
}

// Extract hashtags from game (name + alternate names if available)
const extractGameHashtags = (game) => {
    if (!game) return []

    const hashtags = new Set()

    // Add main game name as hashtag
    if (game.name) {
        const hashtag = gameNameToHashtag(game.name)
        if (hashtag && hashtag.length >= 3) {
            hashtags.add(hashtag)
        }
    }

    // Add alternate names as hashtags (if available from IGDB)
    if (game.alternative_names && Array.isArray(game.alternative_names)) {
        game.alternative_names.forEach((altName) => {
            if (altName && altName.name) {
                const hashtag = gameNameToHashtag(altName.name)
                if (hashtag && hashtag.length >= 3) {
                    hashtags.add(hashtag)
                }
            }
        })
    }

    return Array.from(hashtags)
}

const getSavedHashtagEntries = (options = {}) => {
    if (!Array.isArray(options.hashtags)) {
        return []
    }

    return options.hashtags.filter(entry => entry?.hashtag)
}

const getCopiedPlatformIds = (options = {}) => {
    if (Array.isArray(options.platformIds) && options.platformIds.length > 0) {
        return options.platformIds
    }

    if (options.platformId) {
        return Array.isArray(options.platformId) ? options.platformId : [options.platformId]
    }

    return []
}

const getSavedGameHashtags = (options = {}) => {
    if (!Array.isArray(options.gameHashtagsForNonGamePlatforms)) {
        const savedEntriesByHashtag = _.groupBy(getSavedHashtagEntries(options), 'hashtag')
        const copiedPlatformIds = getCopiedPlatformIds(options)
        const activeNonGamePlatforms = copiedPlatformIds.filter((platformId) => {
            return nonGamePlatformIds.includes(platformId)
        })
        const activeGameFilteringPlatforms = copiedPlatformIds.filter((platformId) => {
            return gameFilteringPlatformIds.includes(platformId)
        })

        if (activeNonGamePlatforms.length === 0 || activeGameFilteringPlatforms.length === 0) {
            return []
        }

        return Object.entries(savedEntriesByHashtag)
            .filter(([, entries]) => {
                const savedPlatforms = _.uniq(entries.map((entry) => {
                    return entry.platform_id
                }).filter(Boolean))
                const appearsOnAllNonGamePlatforms = activeNonGamePlatforms.every((platformId) => {
                    return savedPlatforms.includes(platformId)
                })
                const appearsOnGameFilteringPlatform = activeGameFilteringPlatforms.some((platformId) => {
                    return savedPlatforms.includes(platformId)
                })

                return appearsOnAllNonGamePlatforms && !appearsOnGameFilteringPlatform
            })
            .map(([hashtag]) => {
                return hashtag
            })
    }

    return _.uniq(options.gameHashtagsForNonGamePlatforms.filter(Boolean))
}

const applyCopiedHashtags = (options = {}) => {
    if (Array.isArray(options.hashtags) && options.hashtags.length > 0) {
        const savedGameHashtags = new Set(getSavedGameHashtags(options))
        const uniqueHashtags = _.uniq(getSavedHashtagEntries(options).map(entry => entry.hashtag))

        filters.value.tags = uniqueHashtags
            .filter(hashtag => !savedGameHashtags.has(hashtag))
            .map(tag => ({ id: tag }))
        return
    }

    if (Array.isArray(options.tags)) {
        filters.value.tags = _.map(options.tags, (tag) => {
            return { id : tag }
        })
        return
    }

    filters.value.tags = []
}

// Computed property to get game hashtags for non-game platforms
const gameHashtagsForNonGamePlatforms = computed(() => {
    // Only generate hashtags if game is selected and non-game platforms are included
    const hasNonGamePlatforms = selectedPlatformIds.value.some(id => nonGamePlatformIds.includes(id))

    if (!filters.value.game || !hasNonGamePlatforms) {
        return []
    }

    const allHashtags = extractGameHashtags(filters.value.game)

    // Filter to only include enabled hashtags
    return allHashtags.filter(hashtag => enabledGameHashtags.value.has(hashtag))
})

// Computed property to get Twitch game info
const twitchGameInfo = computed(() => {
    if (!filters.value.game?._platforms?.twitch?.id || typeof filters.value.game._platforms.twitch.id !== 'string') {
        return null
    }

    const gameName = filters.value.game.name || ''
    // Convert game name to slug format for Twitch URL (lowercase, spaces to hyphens, remove special chars)
    const slug = gameName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

    return {
        id: filters.value.game._platforms.twitch.id,
        name: gameName,
        slug: slug,
        url: `https://www.twitch.tv/directory/category/${slug}`
    }
})

// Computed property to get YouTube game info
const youtubeGameInfo = computed(() => {
    // First check if we have manual channel ID from user input
    if (manualYouTubeChannelId.value) {
        return {
            id: manualYouTubeChannelId.value,
            name: filters.value.game?.name || '',
            url: `https://www.youtube.com/channel/${manualYouTubeChannelId.value}`
        }
    }

    // Otherwise check if we have platform linking data
    if (!filters.value.game?._platforms?.youtube?.id || typeof filters.value.game._platforms.youtube.id !== 'string') {
        return null
    }

    return {
        id: filters.value.game._platforms.youtube.id,
        name: filters.value.game.name || '',
        url: `https://www.youtube.com/channel/${filters.value.game._platforms.youtube.id}`
    }
})

// Function to parse YouTube channel ID from URL
const parseYouTubeChannelId = (url) => {
    if (!url) return null

    try {
        // Remove whitespace
        url = url.trim()

        // Handle various YouTube URL formats
        // https://www.youtube.com/channel/UCzIPHpgGkiW5ilaF2dSluaQ
        // https://youtube.com/channel/UCzIPHpgGkiW5ilaF2dSluaQ
        // youtube.com/channel/UCzIPHpgGkiW5ilaF2dSluaQ
        const channelMatch = url.match(/youtube\.com\/channel\/([a-zA-Z0-9_-]+)/i)
        if (channelMatch && channelMatch[1]) {
            return channelMatch[1]
        }

        // Also handle youtu.be format (though less common for channels)
        // And handle just the channel ID itself
        if (/^[a-zA-Z0-9_-]{24}$/.test(url)) {
            return url
        }

        return null
    } catch (error) {
        console.error('Error parsing YouTube channel URL:', error)
        return null
    }
}

const isInitialLoad = ref(false)

// Watch for manual YouTube channel URL changes
watch(manualYouTubeChannelUrl, (newUrl) => {
    const parsedId = parseYouTubeChannelId(newUrl)
    manualYouTubeChannelId.value = parsedId

    // Clear manual input if game changes
    if (!filters.value.game) {
        manualYouTubeChannelUrl.value = ''
        manualYouTubeChannelId.value = null
    }
})

// Computed property to get hashtag platform mappings
const hashtagPlatformMappings = computed(() => {
    if (!filters.value.game) return []

    const hashtags = extractGameHashtags(filters.value.game)
    const activeNonGamePlatforms = selectedPlatformIds.value.filter(id => nonGamePlatformIds.includes(id))

    if (hashtags.length === 0 || activeNonGamePlatforms.length === 0) return []

    const mappings = []
    hashtags.forEach(hashtag => {
        // Include all hashtags, not just enabled ones (we'll use opacity for disabled ones)
        const platformData = []

        activeNonGamePlatforms.forEach(platformId => {
            let url = ''
            switch (platformId) {
                case 'tiktok':
                    url = `https://www.tiktok.com/tag/${hashtag}`
                    break
                case 'instagram':
                    // URL encode the hashtag with # prefix
                    url = `https://www.instagram.com/explore/search/keyword/?q=%23${encodeURIComponent(hashtag)}`
                    break
                case 'twitter':
                case 'x':
                    url = `https://x.com/hashtag/${hashtag}`
                    break
            }

            platformData.push({
                platformId: platformId,
                hashtag: hashtag,
                url: url
            })
        })

        if (platformData.length > 0) {
            mappings.push({
                hashtag: hashtag,
                platforms: platformData
            })
        }
    })

    return mappings
})

// Watch for game changes to initialize enabled hashtags
watch(() => filters.value.game, (newGame) => {
    if (newGame) {
        const allHashtags = extractGameHashtags(newGame)
        if (pendingDefaultOptions.value !== null) {
            // Copying a report - only enable hashtags that were active in the original
            const opts = pendingDefaultOptions.value
            pendingDefaultOptions.value = null
            const savedGameHashtags = new Set(getSavedGameHashtags(opts))

            enabledGameHashtags.value = new Set(allHashtags.filter(hashtag => savedGameHashtags.has(hashtag)))
            applyCopiedHashtags(opts)
        } else {
            enabledGameHashtags.value = new Set(allHashtags)
        }
    } else {
        enabledGameHashtags.value = new Set()
        // Clear manual YouTube channel input when game is cleared
        manualYouTubeChannelUrl.value = ''
        manualYouTubeChannelId.value = null
    }
}, { immediate: true })

// Function to toggle individual hashtag
const toggleGameHashtag = (hashtag) => {
    if (enabledGameHashtags.value.has(hashtag)) {
        enabledGameHashtags.value.delete(hashtag)
    } else {
        enabledGameHashtags.value.add(hashtag)
    }
    // Create a new Set to trigger reactivity
    enabledGameHashtags.value = new Set(enabledGameHashtags.value)
}

const hashtagSupportedPlatforms = computed(() => {
    return ['twitch', 'youtube', 'twitter', 'instagram', 'tiktok']
})

const mentionsSupportedPlatforms = computed(() => {
    return ['instagram']
})

const hasGameFilteringPlatformSelected = computed(() => {
    return selectedPlatformIds.value.some((platformId) => {
        return gameFilteringPlatformIds.includes(platformId)
    })
})

// Helper to get platform icon color class
const getPlatformIconClass = (platformId) => {
    const colorMap = {
        twitch: 'text-twitch',
        youtube: 'text-youtube',
        instagram: 'text-instagram',
        twitter: 'text-x',
        tiktok: 'text-tiktok',
    }
    return colorMap[platformId] || 'text-slate-600'
}

const getPlatformIcon = (platformId) => (platformId === 'twitter' ? 'x-twitter' : platformId)
const getPlatformDisplayName = (platformId) => (platformId === 'twitter' ? 'X' : _.capitalize(platformId))
const getPlatformLabel = (platformId) => {
    return _.find(platforms.value, { id: platformId })?.label || _.startCase(platformId)
}

const hasAdditionalLinks = computed(() => filters.value.additionalContent?.length > 0)
const hasDetectedAdditionalLinkPlatforms = computed(() => detectedAdditionalContentPlatformIds.value.length > 0)
const hasValidDateRange = computed(() => {
    return (
        filters.value.startAt &&
        filters.value.endAt &&
        moment(filters.value.startAt).isSameOrBefore(moment(filters.value.endAt))
    )
})
const hasDateRangeError = computed(() => {
    return (
        !isLinksOnlyMode.value &&
        filters.value.startAt &&
        filters.value.endAt &&
        moment(filters.value.startAt).isAfter(moment(filters.value.endAt))
    )
})

const additionalContentLabel = computed(() => {
    return isLinksOnlyMode.value ? 'Report Links' : 'Additional Links'
})

const additionalContentButtonLabel = computed(() => {
    return hasAdditionalLinks.value ? 'Edit Links' : 'Add Links'
})

const additionalContentDescription = computed(() => {
    if (isLinksOnlyMode.value) {
        return 'Add specific content links or IDs to include in this report.'
    }

    return 'Add specific content links or IDs to include alongside the filtered results for the selected platforms.'
})

const additionalContentModalTitle = computed(() => {
    return isLinksOnlyMode.value ? 'Add Report Links' : 'Add Additional Links'
})

const additionalContentModalDescription = computed(() => {
    if (isLinksOnlyMode.value) {
        return 'Paste the content links you want included in this links-only report, one per line.'
    }

    return 'Add content links or IDs to include alongside the filtered results, one per line.'
})

const footerMessage = computed(() => {
    if (props.isLoading === true) {
        return 'Generating report...'
    }

    if (hasDateRangeError.value) {
        return 'Start date must be before the end date.'
    }

    if (isLinksOnlyMode.value) {
        if (!hasAdditionalLinks.value) {
            return 'Add at least one content link to generate this report.'
        }

        if (!hasDetectedAdditionalLinkPlatforms.value) {
            return 'We could not detect a supported platform from those links. Paste full content links.'
        }

        return 'Ready to generate a links-only report.'
    }

    if (selectedPlatformIds.value.length === 0) {
        return 'Choose at least one platform to continue.'
    }

    if (!filters.value.startAt || !filters.value.endAt) {
        return 'Choose a date range to continue.'
    }

    const hasAnyStandardFilter =
        !!filters.value.game ||
        filters.value.tags?.length > 0 ||
        filters.value.taggedUserNames?.length > 0 ||
        filters.value.listId?.length > 0

    if (!hasAnyStandardFilter) {
        return `Choose a game, hashtag${selectedPlatformIds.value.includes('instagram') ? ', mentions' : ''}, or list.`
    }

    return hasAdditionalLinks.value
        ? 'Ready to generate a report with additional links.'
        : 'Ready to generate a report.'
})

const detectedAdditionalPlatformLabels = computed(() => {
    return detectedAdditionalContentPlatformIds.value.map(getPlatformLabel)
})

// Computed to check if multiple filters are present
const hasMultipleFilters = computed(() => {
    const hasGame = !!filters.value.game
    const hasHashtags = filters.value.tags && filters.value.tags.length > 0
    const hasMentions = filters.value.taggedUserNames && filters.value.taggedUserNames.length > 0

    // Count how many filters are present
    let filterCount = 0
    if (hasGame) filterCount++
    if (hasHashtags) filterCount++
    if (hasMentions) filterCount++

    return filterCount >= 2
})


const canGenerateReport = computed(() => {
    // Must have at least one platform selected
    if (!isLinksOnlyMode.value && selectedPlatformIds.value.length === 0) {
        return false
    }

    const hasValidTaggedUsers = selectedPlatformIds.value.includes('instagram') &&
                               filters.value.taggedUserNames &&
                               filters.value.taggedUserNames.length > 0

    // For multi-platform, game filter only applies to youtube/twitch
    const hasGameFilter = filters.value.game && hasGameFilteringPlatformSelected.value

    const hasStandardFilters =
        hasGameFilter ||
        filters.value.tags?.length > 0 ||
        filters?.value?.listId?.length > 0 ||
        hasValidTaggedUsers
    const hasPermissionForMode = isReportsEnabled.value || (!isLinksOnlyMode.value && filters?.value?.listId?.length > 0)

    return (
        props.isLoading === false &&
        (
            (!isLinksOnlyMode.value && hasStandardFilters && hasValidDateRange.value) ||
            (isLinksOnlyMode.value && hasAdditionalLinks.value && hasDetectedAdditionalLinkPlatforms.value)
        ) &&
        hasPermissionForMode
    )
})

watch(selectedPlatformIds, () => {
    if(isInitialLoad.value === true) {
        isInitialLoad.value = false
        return
    }

    sponsorshipConfig.value = defaultSponsorshipConfig()
    filters.value.isIgnoreTiny = true

    // Always default to "Any" (empty string) for content type
    filters.value.contentType = ''

    // Clear game if it doesn't match any selected platform
    if (filters.value.game &&
        !hasGameFilteringPlatformSelected.value) {
        defaultSelectedGame.value = []
        filters.value.game = null
    }

    // Update localPlatformId for backward compatibility (use first selected)
    if (selectedPlatformIds.value.length > 0) {
        localPlatformId.value = selectedPlatformIds.value[0]
    } else {
        localPlatformId.value = null
    }

    // Emit even if empty so parent knows selection was cleared
    emit('platform-change', [...selectedPlatformIds.value])
}, { immediate: false })

watch(localPlatformId, () => {
    // Keep selectedPlatformIds in sync with localPlatformId for backward compatibility
    if (localPlatformId.value && !selectedPlatformIds.value.includes(localPlatformId.value)) {
        selectedPlatformIds.value = [localPlatformId.value]
    }
}, { immediate: true })

watch(filters, () => {
    filtersHaveChanged.value = true
}, { deep : true, immediate: true })

const reset = () => {
    filters.value = {
        ...filtersDefault,
        contentType: '', // Always default to "Any" (empty string)
    }
    additionalContent.value = ''
    detectedAdditionalContentPlatformIds.value = []
    sponsorshipConfig.value = defaultSponsorshipConfig()
    filters.value.isIgnoreTiny = true
    defaultSelectedGame.value = []
    initialSelectedList.value = null
    listSources?.value?.clear()
    selectedPlatformIds.value = getInitialPlatformIds().length > 0 ? [...getInitialPlatformIds()] : []
    manualYouTubeChannelUrl.value = ''
    manualYouTubeChannelId.value = null

    setFilterStatus(false)
}

const initialize = () => {
    reset()

    if (props?.defaultTitle) {
        filters.value.title = props.defaultTitle
    }

    if (props?.defaultReportOptions) {

        isInitialLoad.value = true
        const optionsToLoadFrom = props.defaultReportOptions
        const hasSavedGameSelection =
            (Array.isArray(optionsToLoadFrom.game_ids) && optionsToLoadFrom.game_ids.length > 0) ||
            optionsToLoadFrom.gameId ||
            optionsToLoadFrom.igdbGameId

        // Support both platformId (single) and platformIds (array)
        if (optionsToLoadFrom.platformIds && Array.isArray(optionsToLoadFrom.platformIds)) {
            selectedPlatformIds.value = optionsToLoadFrom.platformIds
            localPlatformId.value = optionsToLoadFrom.platformIds[0] || getInitialPlatformIds()[0] || null
        } else if (optionsToLoadFrom.platformId) {
            selectedPlatformIds.value = [optionsToLoadFrom.platformId]
            localPlatformId.value = optionsToLoadFrom.platformId
        }
        filters.value.contentType = (optionsToLoadFrom.contentType && optionsToLoadFrom.contentType !== null) ? optionsToLoadFrom.contentType : ''
        filters.value.startAt = optionsToLoadFrom.startAt ? optionsToLoadFrom.startAt : filters.value.startAt
        filters.value.endAt = optionsToLoadFrom.endAt ? optionsToLoadFrom.endAt : filters.value.endAt
        filters.value.isIgnoreTiny = optionsToLoadFrom.hasOwnProperty('isIgnoreTiny') ? optionsToLoadFrom.isIgnoreTiny : filters.value.isIgnoreTiny
        additionalContent.value = optionsToLoadFrom.additionalContent ? optionsToLoadFrom.additionalContent.join('\n') : additionalContent.value
        filters.value.additionalContent = optionsToLoadFrom.additionalContent
        syncAdditionalContentDetails(selectedPlatformIds.value)
        setLinksOnlyMode(optionsToLoadFrom.additionalContentOnly === true)


        if (optionsToLoadFrom.sponsorshipConfig) {
            sponsorshipConfig.value.changed = optionsToLoadFrom.sponsorshipConfig.hasOwnProperty('changed') ? optionsToLoadFrom.sponsorshipConfig.changed : sponsorshipConfig.value.changed
            sponsorshipConfig.value.mustMatchGame = optionsToLoadFrom.sponsorshipConfig.hasOwnProperty('mustMatchGame') ? optionsToLoadFrom.sponsorshipConfig.mustMatchGame : sponsorshipConfig.value.mustMatchGame
            sponsorshipConfig.value.includeShoutouts = optionsToLoadFrom.sponsorshipConfig.hasOwnProperty('includeShoutouts') ? optionsToLoadFrom.sponsorshipConfig.includeShoutouts : sponsorshipConfig.value.includeShoutouts
            sponsorshipConfig.value.enabled = optionsToLoadFrom.sponsorshipConfig.hasOwnProperty('enabled') ? optionsToLoadFrom.sponsorshipConfig.enabled : sponsorshipConfig.value.enabled
        }

        const copiedPlatformIds = getCopiedPlatformIds(optionsToLoadFrom)
        const hasCopiedNonGamePlatform = copiedPlatformIds.some((platformId) => {
            return nonGamePlatformIds.includes(platformId)
        })
        const shouldDeferCopiedHashtags =
            hasSavedGameSelection &&
            hasCopiedNonGamePlatform

        if (!shouldDeferCopiedHashtags) {
            applyCopiedHashtags(optionsToLoadFrom)
        }

        // Handle mentions: new format (array) or old format (array of strings)
        if (optionsToLoadFrom.mentions && Array.isArray(optionsToLoadFrom.mentions) && optionsToLoadFrom.mentions.length > 0) {
            // New format: [{ platform_id, mention }]
            const instagramMentions = optionsToLoadFrom.mentions
                .filter(m => m.platform_id === 'instagram')
                .map(m => m.mention)
            filters.value.taggedUserNames = _.uniq(instagramMentions)
        } else if (optionsToLoadFrom.taggedUserNames && Array.isArray(optionsToLoadFrom.taggedUserNames)) {
            // Old format: array of strings
            filters.value.taggedUserNames = optionsToLoadFrom.taggedUserNames
        } else {
            filters.value.taggedUserNames = []
        }

        // Handle hashtagGameFilterLogic
        if (optionsToLoadFrom.hashtagGameFilterLogic) {
            filters.value.hashtagGameFilterLogic = optionsToLoadFrom.hashtagGameFilterLogic
        }

        if (optionsToLoadFrom.listId) {
            initialSelectedList.value = {  type : 'list', input : optionsToLoadFrom.listId }
            filters.value.listId = optionsToLoadFrom.listId
        }

        // Handle game: new format (game_ids array) or old format (gameId/igdbGameId)
        if (optionsToLoadFrom.game_ids && Array.isArray(optionsToLoadFrom.game_ids) && optionsToLoadFrom.game_ids.length > 0) {
            // New format: [{ platform_id, game_id }]
            const gameIdsArray = optionsToLoadFrom.game_ids
            const igdbGame = gameIdsArray.find(g => g.platform_id === 'igdb')

            if (igdbGame) {
                defaultSelectedGame.value = [igdbGame.game_id]
            }
            else {
                defaultSelectedGame.value = [{
                    missingIGDB : true,
                    platforms : gameIdsArray
                }]
            }
        } else if (optionsToLoadFrom.gameId || optionsToLoadFrom.igdbGameId) {
            // Old format: single gameId or igdbGameId
            if (optionsToLoadFrom.igdbGameId) {
                defaultSelectedGame.value = [optionsToLoadFrom.igdbGameId]
            }
            else if (optionsToLoadFrom.gameId) {
                defaultSelectedGame.value = [{
                    missingIGDB : true,
                    platforms : [{
                        platform_id : optionsToLoadFrom.platformId || selectedPlatformIds.value[0],
                        game_id : optionsToLoadFrom.gameId
                    }]
                }]
            }
        }
        pendingDefaultOptions.value = shouldDeferCopiedHashtags ? optionsToLoadFrom : null
        setFilterStatus(true)
    }
    else {
        setFilterStatus(false)
    }
}

const setFilterStatus = (changed) => {
    //There is probably a sane way of ensuring this runs last...
    nextTick(()=>{
        nextTick(()=>{
            nextTick(()=>{
                filtersHaveChanged.value = changed === true
            })
        })
    })
}

const onPlatformToggle = (platformId) => {
    const index = selectedPlatformIds.value.indexOf(platformId)
    if (index > -1) {
        selectedPlatformIds.value.splice(index, 1)
    } else {
        selectedPlatformIds.value.push(platformId)
    }

    // Update localPlatformId to first selected, or null if empty
    if (selectedPlatformIds.value.length > 0) {
        localPlatformId.value = selectedPlatformIds.value[0]
    } else {
        localPlatformId.value = null
    }

    // Emit a copy of the array to avoid reactivity issues
    emit('platform-change', [...selectedPlatformIds.value])
}

const selectAllPlatforms = () => {
    // Toggle: if all are selected, deselect all; otherwise select all
    const allSelected = platforms.value.every(p => selectedPlatformIds.value.includes(p.id))
    if (allSelected) {
        selectedPlatformIds.value = []
        localPlatformId.value = null
    } else {
        selectedPlatformIds.value = [...platforms.value.map(p => p.id)]
        if (selectedPlatformIds.value.length > 0) {
            localPlatformId.value = selectedPlatformIds.value[0]
        }
    }
    // Emit a copy of the array to avoid reactivity issues
    emit('platform-change', [...selectedPlatformIds.value])
}

const onChangeSponsorshipConfig = () => {
    sponsorshipConfig.value.changed = true
}

const groupListsByPlatform = (items) => {
    const platformsToGroupBy = ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']
    const groupedByPlatform = {}

    _.forEach(platformsToGroupBy, (platformId) => {
        groupedByPlatform[platformId] = []
    })

    _.forEach(items, (item) => {
        _.forEach(platformsToGroupBy, (platformId) => {
            if (_.get(item, `totals.${platformId}`, 0) > 0) {
                groupedByPlatform[platformId].push(item)
            }
        })
    })

    return groupedByPlatform
}

const listsByPlatformId = ref({
    twitch: [],
    youtube: [],
    tiktok: [],
    instagram: [],
    twitter: [],
})

const getLists = async () => {
    return axios
        .get('/lists')
        .then(({ data }) => {
            let lists = _.map(data.payload.myLists, (list) => {
                // eslint-disable-next-line no-param-reassign
                list.type = 'user'
                return list
            })
            const allLists = [...lists, ...data.payload.sharedLists]
            const groupedById = groupListsByPlatform(allLists)

            if (!props?.optionsToLoadFrom?.value && props.defaultListId && _.find(allLists, list => { return list.id === props.defaultListId})) {
                initialSelectedList.value = {
                    type : 'list',
                    input : props.defaultListId,
                }
                filters.value.listId = props.defaultListId

            }

            if (initialSelectedList.value?.input && !_.find(allLists, list => list.id === initialSelectedList.value.input)) {
                const unknownList = { id: initialSelectedList.value.input, name: 'Unknown list', isUnknown: true }
                _.forEach(groupedById, platformLists => platformLists.push(unknownList))
            }

            listsByPlatformId.value = groupedById

        })
        .catch((error) => {
            console.log(error)
        })
}

getLists()

const onListSourceChange = (list) => {
    filters.value.listId = list?.value?.id
}

const onOpenModal = () => {
    additionalContentOpen.value = true
}

const onCloseModal = () => {
    syncAdditionalContentDetails()
    additionalContentOpen.value = false
}

const onGenerateReport = () => {

    // Keep sponsorship detection explicit so copies and report views preserve the chosen state.
    sponsorshipConfig.value.mustMatchGame = !!(sponsorshipConfig.value.mustMatchGame && filters.value.game)
    filters.value.sponsorshipConfig = sponsorshipConfig.value

    const platformIdsForReport = isLinksOnlyMode.value
        ? detectedAdditionalContentPlatformIds.value
        : selectedPlatformIds.value

    filters.value.additionalContentOnly = isLinksOnlyMode.value
    filters.value.platformIds = platformIdsForReport
    // Keep platformId for backward compatibility (use first selected)
    filters.value.platformId = platformIdsForReport[0] || null

    // Add game hashtags for non-game platforms
    filters.value.gameHashtagsForNonGamePlatforms = gameHashtagsForNonGamePlatforms.value

    // Add manual YouTube channel ID if provided
    if (manualYouTubeChannelId.value) {
        filters.value.manualYouTubeChannelId = manualYouTubeChannelId.value
    }

    emit('filter-change', filters.value, props.editReportId)

    IGDBSelectKey.value = Date.now() //Reset the game control, this should be refactored
}

const getUrlsFromAdditionalContent = () => {
    return additionalContent.value
            .split('\n')
            .map((item) => item.trim())
            .filter((query) => query !== '')
}

const extractYouTube = (urls) => {
    return _.map(urls, url => {
            if (url.trim().length === 11) {
                let alphaNumericMatcher = new RegExp(/^[a-z0-9_-]+$/i)
                if (alphaNumericMatcher.exec(url)) {
                    return url
                }
            }
            let youtubeMatcher = new RegExp(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|(shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/gi)
            let matched = youtubeMatcher.exec(url)
            if (matched?.length > 2) {
                return matched[2]
            }
            return ''
    })
}

const extractTwitch = (urls) => {
    return _.map(urls, url => {
        let twitchMatcher = new RegExp('^[0-9]+$')
        let matched = twitchMatcher.exec(url)
        if (matched) {
            return matched[0]
        }
        return ''
    })
}
const extractInstagram = (urls) => {
    return _.map(urls, url => {
        if (url.trim().length === 11) {
            let alphaNumericMatcher = new RegExp(/^[a-z0-9_-]+$/i)
            if (alphaNumericMatcher.exec(url)) {
                return url
            }
        }
        let instagramMatcher = new RegExp(/(?:https?:\/\/www\.)?instagram\.com\S*?\/p\/([a-zA-Z0-9-_]{11})/gi)
        let matched = instagramMatcher.exec(url)
        if (matched?.length > 1) {
            return matched[1]
        }

        // Instagram links reels as both /reel/ and /reels/ (some share sheets hand out the plural)
        let instagramReelMatcher = new RegExp(/(?:https?:\/\/www\.)?instagram\.com\S*?\/reels?\/([a-zA-Z0-9-_]{11})/gi)
        matched = instagramReelMatcher.exec(url)
        if (matched?.length > 1) {
            return matched[1]
        }

        return ''
    })
}
const extractTikTok = (urls) => {
    return _.map(urls, url => {
        if (url.trim().length === 19) {
            let alphaNumericMatcher = new RegExp(/^[0-9]+$/i)
            if (alphaNumericMatcher.exec(url)) {
                return url
            }
        }
        let tiktokMatcher = new RegExp(/^.*https:\/\/(?:m|www|vm)?\.?tiktok\.com\/((?:.*\b(?:(?:usr|v|embed|user|video)\/|\?shareId=|\&item_id=)(\d+))|\w+)/gi)
        let matched = tiktokMatcher.exec(url)
        if (matched?.length > 2) {
            return matched[2]
        }
        return ''
    })
}
const extractX = (urls) => {
    return _.map(urls, url => {
        if (url.trim().length === 19) {
            let alphaNumericMatcher = new RegExp(/^[0-9]+$/i)
            if (alphaNumericMatcher.exec(url)) {
                return url
            }
        }
        let xMatcher = new RegExp(/^https?:\/\/(?:twitter|x)\.com\/(?:#!\/)?(\w+)\/status(?:es)?\/(\d+)(?:[/?#].*)?$/gi)
        let matched = xMatcher.exec(url)
        if (matched?.length > 2) {
            return matched[2]
        }
        return ''
    })
}
const extractAdditionalContentDetails = () => {
    let uniqueMatchedIds = []
    let matchedPlatformIds = []

    try {
        const urls = getUrlsFromAdditionalContent()
        const matchesByPlatform = {
            youtube: _.uniq(extractYouTube(urls).filter((query) => query !== '')),
            twitch: _.uniq(extractTwitch(urls).filter((query) => query !== '')),
            instagram: _.uniq(extractInstagram(urls).filter((query) => query !== '')),
            tiktok: _.uniq(extractTikTok(urls).filter((query) => query !== '')),
            twitter: _.uniq(extractX(urls).filter((query) => query !== '')),
        }

        uniqueMatchedIds = _.uniq(_.flatten(Object.values(matchesByPlatform)))
        matchedPlatformIds = Object.entries(matchesByPlatform)
            .filter(([, matches]) => matches.length > 0)
            .map(([platformId]) => platformId)

    }
    catch(ex) {
        console.log(ex)
    }

    return {
        ids: uniqueMatchedIds,
        platformIds: matchedPlatformIds,
    }
}

const onResetOptions = () => {
    reset()
}

initialize()
</script>

<template>
    <div class="flex min-h-0 flex-1 flex-col">
        <DialogModal :open="additionalContentOpen" @update:open="onCloseModal">
            <DialogContent
                class="max-w-2xl"
                @interact-outside="(event) => event.preventDefault()"
            >
                <DialogHeader>
                    <DialogTitle>{{ additionalContentModalTitle }}</DialogTitle>
                    <DialogDescription>
                        {{ additionalContentModalDescription }}
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Textarea
                        v-model="additionalContent"
                        class="min-h-64"
                        placeholder="Enter content links or IDs, one per line..."
                    />
                </div>
                <DialogFooter>
                    <Button variant="ghost" @click="additionalContent = ''">
                        Clear
                    </Button>
                    <Button @click="onCloseModal">
                        Done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </DialogModal>

        <div class="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
            <div class="grid min-h-[910px] grid-cols-1 content-start gap-5 md:grid-cols-2">

                <!-- Report Title -->
                <div class="col-span-2">
                    <Label class="mb-1.5 block">Report Title</Label>
                    <Input
                        v-model="filters.title"
                        type="text"
                        placeholder="Enter a title for your report"
                        maxlength="100"
                    />
                </div>

                <div class="col-span-2 rounded-md border border-slate-200 bg-slate-50 p-4 md:col-span-1">
                    <div class="flex items-start justify-between gap-4">
                        <div>
                            <Label for="additional-content-only" class="block text-sm font-medium">
                                Build report from links only
                            </Label>
                            <p class="mt-1 text-xs text-slate-500">
                                Skip discovery filters and use only the links you add.
                            </p>
                        </div>
                        <Switch
                            id="additional-content-only"
                            :checked="isLinksOnlyMode"
                            @update:checked="setLinksOnlyMode"
                        />
                    </div>
                </div>

                <!-- Start and End Date -->
                <div v-if="!isLinksOnlyMode" class="col-span-2 grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                        <Label class="mb-1.5 block">Start Date</Label>
                        <flatPickr
                            v-model="filters.startAt"
                            :config="startPickerConfig"
                            placeholder="Start Date"
                            class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                    </div>

                    <div>
                        <Label class="mb-1.5 block">End Date</Label>
                        <flatPickr
                            v-model="filters.endAt"
                            :config="endPickerConfig"
                            placeholder="End Date"
                            class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                    </div>
                </div>

                <div v-if="!isLinksOnlyMode" class="col-span-2 border-t border-slate-100"></div>

                <!-- Platforms -->
                <div v-if="!isLinksOnlyMode" class="col-span-2">
                    <div class="flex items-center gap-2 mb-1.5">
                        <Label>Platforms</Label>
                        <span class="text-xs text-slate-400">·</span>
                        <button
                            class="text-xs text-primary hover:underline"
                            @click="selectAllPlatforms"
                        >
                            {{ platforms.every(p => selectedPlatformIds.includes(p.id)) ? 'Deselect all' : 'Select all' }}
                        </button>
                    </div>

                    <div class="flex flex-wrap gap-2">
                        <label
                            v-for="platform in platforms"
                            :key="platform.id"
                            class="flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 transition-colors"
                            :class="selectedPlatformIds.includes(platform.id)
                                ? 'bg-slate-50 border-slate-300'
                                : 'border-slate-200 hover:bg-slate-50'"
                        >
                            <Checkbox
                                :checked="selectedPlatformIds.includes(platform.id)"
                                @update:checked="onPlatformToggle(platform.id)"
                            />
                            <span class="text-sm font-medium">{{ platform.label }}</span>
                        </label>
                    </div>
                </div>

                <!-- Game filter - visually distinct -->
                <div v-if="!isLinksOnlyMode && hasGameFilteringPlatformSelected" class="col-span-2">
                    <div class="mb-1 flex items-center justify-between">
                        <Label>IGDB Game</Label>
                    </div>
                    <!-- Use IGDBGameSelect when both Twitch and YouTube are selected -->
                    <IGDBGameSelect
                        :key="`igdb-${IGDBSelectKey}`"
                        :selected-options="defaultSelectedGame"
                        search-placeholder="Search for a game"
                        @selection-change="(item) => (filters.game = $lodash.first(item))"
                    />

                    <!-- Split view for IGDB game details (only show when IGDB game is selected) -->
                    <div
                        v-if="filters.game && hasGameFilteringPlatformSelected"
                        class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2"
                    >
                        <!-- Left side: Twitch & YouTube game info -->
                        <div class="space-y-2">
                            <div class="flex items-center gap-1.5">
                                <div class="text-xs font-semibold text-slate-600 uppercase tracking-wide">Games</div>
                                <Tooltip>
                                    <template #trigger>
                                        <AppIcon
                                            name="circle-info"
                                            class="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help"
                                        />
                                    </template>
                                    <template #default>
                                        <div class="text-xs max-w-xs">
                                            <div class="font-semibold mb-1">Game Filtering:</div>
                                            <p class="mb-2">For platforms that support tagging content as being associated with a video game (Twitch, YouTube), we can do direct matching.</p>
                                            <p class="mb-1">Streamforge uses IGDB data as a baseline and has a system that automatically connects YouTube's games to the IGDB data, but it is not perfect and sometimes requires manual intervention by our team to fix edge cases.</p>
                                            <p class="text-amber-200 mt-1">Reach out to support if you spot any miss-linking.</p>
                                        </div>
                                    </template>
                                </Tooltip>
                            </div>

                            <!-- Twitch Game -->
                            <div v-if="selectedPlatformIds.includes('twitch')" class="rounded border border-slate-200 bg-white px-3 py-2">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-2">
                                        <AppIcon name="twitch" class="h-4 w-4 text-twitch" />
                                        <span class="text-sm font-medium">{{ twitchGameInfo?.name || filters.game?.name || 'Twitch Game' }}</span>
                                    </div>
                                    <a
                                        v-if="twitchGameInfo"
                                        :href="twitchGameInfo.url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="text-slate-400 hover:text-slate-600"
                                        title="View on Twitch"
                                    >
                                        <AppIcon name="arrow-up-right-from-square" class="h-3.5 w-3.5" />
                                    </a>
                                </div>
                                <!-- Notice for missing Twitch game ID -->
                                <div v-if="!twitchGameInfo && filters.game" class="mt-2 rounded bg-amber-50 border border-amber-200 px-2 py-1.5">
                                    <div class="flex items-start gap-2">
                                        <AppIcon name="circle-exclamation" class="h-3.5 w-3.5 text-amber-600 mt-0.5" />
                                        <div class="text-xs text-amber-800">
                                            <div class="font-semibold">Missing linking data</div>
                                            <div>We are missing linking data in our database for the equivalent of this game on Twitch.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- YouTube Game -->
                            <div v-if="selectedPlatformIds.includes('youtube')" class="rounded border border-slate-200 bg-white px-3 py-2">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-2">
                                        <AppIcon name="youtube" class="h-4 w-4 text-youtube" />
                                        <span class="text-sm font-medium">{{ youtubeGameInfo?.name || filters.game?.name || 'YouTube Game' }}</span>
                                    </div>
                                    <a
                                        v-if="youtubeGameInfo"
                                        :href="youtubeGameInfo.url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="text-slate-400 hover:text-slate-600"
                                        title="View on YouTube"
                                    >
                                        <AppIcon name="arrow-up-right-from-square" class="h-3.5 w-3.5" />
                                    </a>
                                </div>
                                <!-- Notice for missing YouTube game ID -->
                                <div v-if="!filters.game._platforms?.youtube?.id && filters.game" class="mt-2 space-y-2">
                                    <div v-if="!manualYouTubeChannelId" class="rounded bg-amber-50 border border-amber-200 px-2 py-1.5">
                                        <div class="flex items-start gap-2">
                                            <AppIcon name="circle-exclamation" class="h-3.5 w-3.5 text-amber-600 mt-0.5" />
                                            <div class="text-xs text-amber-800">
                                                <div class="font-semibold">Missing linking data</div>
                                                <div>We are missing linking data in our database for the equivalent of this game on YouTube.</div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Manual YouTube channel URL input -->
                                    <div>
                                        <div class="flex items-center gap-1.5 mb-1">
                                            <label class="block text-xs font-medium text-slate-700">
                                                Provide YouTube Channel URL
                                            </label>
                                            <Tooltip>
                                                <template #trigger>
                                                    <AppIcon
                                                        name="circle-info"
                                                        class="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help"
                                                    />
                                                </template>
                                                <template #default>
                                                    <div class="text-xs max-w-xs">
                                                        <div class="font-semibold mb-1">Important:</div>
                                                        <ul class="list-disc list-inside space-y-0.5">
                                                            <li>The YouTube channel should say "Auto-generated by YouTube"</li>
                                                            <li>These pages are not the same as a typical YouTube profile</li>
                                                            <li>Putting a normal YouTube profile link will not work</li>
                                                            <li>Putting the YouTube channel for a game publisher or developer also will not work</li>
                                                        </ul>
                                                    </div>
                                                </template>
                                            </Tooltip>
                                        </div>
                                        <input
                                            v-model="manualYouTubeChannelUrl"
                                            type="text"
                                            placeholder="https://www.youtube.com/channel/UCzIPHpgGkiW5ilaF2dSluaQ"
                                            class="h-8 w-full rounded border border-slate-200 px-2 py-1 text-xs leading-none"
                                            :class="{ 'border-red-300': manualYouTubeChannelUrl && !manualYouTubeChannelId }"
                                        />
                                        <div v-if="manualYouTubeChannelUrl && !manualYouTubeChannelId" class="mt-1 text-xs text-red-600">
                                            Invalid YouTube channel URL. Please provide a URL in the format: https://www.youtube.com/channel/CHANNEL_ID
                                        </div>
                                        <div v-if="manualYouTubeChannelId" class="mt-1 text-xs text-green-600">
                                            ✓ Channel ID parsed: {{ manualYouTubeChannelId }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Right side: Hashtag hierarchy -->
                        <div v-if="hashtagPlatformMappings.length > 0" class="space-y-2">
                            <div class="flex items-center gap-1.5">
                                <div class="text-xs font-semibold text-slate-600 uppercase tracking-wide">Game Hashtags</div>
                                <Tooltip>
                                    <template #trigger>
                                        <AppIcon
                                            name="circle-info"
                                            class="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help"
                                        />
                                    </template>
                                    <template #default>
                                        <div class="text-xs max-w-xs">
                                            <div class="font-semibold mb-1">How Game Hashtags Work:</div>
                                            <p class="mb-2">For platforms that do not support the concept of tagging content as being associated with a game (X, Instagram, TikTok), we use hashtags to match content.</p>
                                            <p class="mb-1">The hashtags shown are auto-generated from the various names that the selected game goes by (including alternate names and translations).</p>
                                            <p>You can enable or disable individual hashtags using the toggle switch on each hashtag.</p>
                                        </div>
                                    </template>
                                </Tooltip>
                            </div>

                            <div v-for="mapping in hashtagPlatformMappings" :key="mapping.hashtag" class="space-y-1.5">
                                <!-- Parent hashtag -->
                                <div class="flex items-center justify-between rounded border border-blue-200 bg-blue-50 px-2 py-1" :class="{ 'opacity-50': !enabledGameHashtags.has(mapping.hashtag) }">
                                    <div class="flex items-center gap-2">
                                        <AppIcon name="hashtag" class="h-3.5 w-3.5 text-blue-600" />
                                        <span class="text-sm font-medium text-blue-800">{{ mapping.hashtag }}</span>
                                    </div>
                                    <Switch
                                        class="origin-right scale-75"
                                        :checked="enabledGameHashtags.has(mapping.hashtag)"
                                        @update:checked="(checked) => toggleGameHashtag(mapping.hashtag)"
                                    />
                                </div>

                                <!-- Child platforms -->
                                <div class="ml-4 space-y-1" :class="{ 'opacity-50': !enabledGameHashtags.has(mapping.hashtag) }">
                                    <div
                                        v-for="platform in mapping.platforms"
                                        :key="`${mapping.hashtag}-${platform.platformId}`"
                                        class="flex items-center justify-between rounded border border-slate-200 bg-white px-2 py-1.5"
                                    >
                                        <div class="flex items-center gap-2">
                                            <AppIcon
                                                :name="platform.platformId"
                                                class="h-3.5 w-3.5"
                                                :class="getPlatformIconClass(platform.platformId)"
                                            />
                                            <span class="text-xs font-medium">{{ platform.hashtag }}</span>
                                        </div>
                                        <a
                                            :href="platform.url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="text-slate-400 hover:text-slate-600"
                                            :title="`View on ${platform.platformId}`"
                                        >
                                            <AppIcon name="arrow-up-right-from-square" class="h-3 w-3" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Info message about hashtag conversion for non-game platforms (only show for single platform CodeSelect) -->
                    <div
                        v-if="filters.game && selectedPlatformIds.some(id => ['twitter', 'instagram', 'tiktok'].includes(id)) && !(selectedPlatformIds.includes('twitch') && selectedPlatformIds.includes('youtube'))"
                        class="mt-2 rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800"
                    >
                        <div class="font-semibold mb-1">ℹ️ Game Filtering on Non-Gaming Platforms</div>
                        <div class="mb-2">
                            For platforms that don't support game filtering (X, Instagram, TikTok),
                            the game name{{ extractGameHashtags(filters.game).length > 1 ? ' and alternate names' : '' }}
                            will be converted to hashtags and used to filter content. Click to enable/disable each hashtag.
                        </div>
                        <div v-if="extractGameHashtags(filters.game).length > 0" class="flex flex-wrap gap-1.5">
                            <button
                                v-for="hashtag in extractGameHashtags(filters.game)"
                                :key="hashtag"
                                type="button"
                                class="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium transition-opacity cursor-pointer"
                                :class="enabledGameHashtags.has(hashtag)
                                    ? 'border-blue-300 bg-blue-100 text-blue-800 opacity-100'
                                    : 'border-blue-200 bg-blue-50 text-blue-600 opacity-40'"
                                @click="toggleGameHashtag(hashtag)"
                            >
                                <AppIcon name="hashtag" class="h-3 w-3" />
                                <span>{{ hashtag }}</span>
                                <AppIcon name="xmark" class="h-3 w-3 ml-0.5" />
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="!isLinksOnlyMode" class="col-span-2 border-t border-slate-100"></div>

                <!-- Hashtag + Mentions row (2 columns) -->
                <div v-if="!isLinksOnlyMode" class="col-span-2 grid grid-cols-1 gap-5 md:grid-cols-2">
                    <!-- Hashtag filter -->
                    <div>
                        <div class="mb-1 flex items-center justify-between">
                            <Label>Hashtag</Label>
                            <div class="flex items-center gap-1.5">
                                <Tooltip v-for="platformId in hashtagSupportedPlatforms" :key="platformId">
                                    <template #trigger>
                                        <AppIcon
                                            :name="getPlatformIcon(platformId)"
                                            class="h-4 w-4"
                                            :class="getPlatformIconClass(platformId)"
                                        />
                                    </template>
                                    <template #default>
                                        <div class="whitespace-nowrap text-xs">
                                            {{ getPlatformDisplayName(platformId) }} supports hashtag filtering
                                        </div>
                                    </template>
                                </Tooltip>
                            </div>
                        </div>
                        <MultiTagInput
                            :model-value="filters.tags"
                            :placeholder="'Type a hashtag'"
                            @update:model-value="(item) => (filters.tags = item)"
                        />
                        <p class="text-xs text-slate-500">Content matching any of the entered hashtags will be included. Case-insensitive, no # prefix needed.</p>
                    </div>

                    <!-- Mentions filter -->
                    <div v-if="selectedPlatformIds.includes('instagram')">
                        <div class="mb-1 flex items-center justify-between">
                            <Label>Mentions</Label>
                            <div class="flex items-center gap-1.5">
                                <Tooltip v-for="platformId in mentionsSupportedPlatforms" :key="platformId">
                                    <template #trigger>
                                        <AppIcon
                                            :name="getPlatformIcon(platformId)"
                                            class="h-4 w-4"
                                            :class="getPlatformIconClass(platformId)"
                                        />
                                    </template>
                                    <template #default>
                                        <div class="whitespace-nowrap text-xs">
                                            {{ getPlatformDisplayName(platformId) }} supports mention filtering
                                        </div>
                                    </template>
                                </Tooltip>
                            </div>
                        </div>
                        <MultiTagInput
                            :model-value="mentionTags"
                            type="mention"
                            :placeholder="'Type a username'"
                            @update:model-value="(items) => updateMentions(items)"
                        />
                        <p class="text-xs text-slate-500">Filter by Instagram @mentions. Enter usernames without the @ symbol.</p>
                    </div>
                </div>

                <!-- Filter Logic -->
                <div
                    v-if="!isLinksOnlyMode && hasMultipleFilters"
                    class="col-span-2"
                >
                    <Label class="mb-1 block">Filter Logic</Label>
                    <select
                        v-model="filters.hashtagGameFilterLogic"
                        class="h-10 w-full rounded border border-slate-200 px-3.5 py-1 text-sm leading-none"
                    >
                        <option value="and">All Filters (must match all selected filters)</option>
                        <option value="or">Any Filter (match any one of the selected filters)</option>
                    </select>
                    <div class="mt-2 text-xs text-slate-600">
                        <span v-if="filters.hashtagGameFilterLogic === 'and'">
                            Content must match <strong>all</strong> selected filters (game{{ filters.tags && filters.tags.length > 0 ? ' + hashtags' : '' }}{{ filters.taggedUserNames && filters.taggedUserNames.length > 0 ? ' + mentions' : '' }}).
                        </span>
                        <span v-else>
                            Content must match <strong>any one</strong> of the selected filters (game{{ filters.tags && filters.tags.length > 0 ? ', hashtags' : '' }}{{ filters.taggedUserNames && filters.taggedUserNames.length > 0 ? ', or mentions' : '' }}).
                        </span>
                    </div>
                </div>

                <div v-if="!isLinksOnlyMode" class="col-span-2 border-t border-slate-100"></div>

                <!-- Content Type + List Source row (2 columns) -->
                <div v-if="!isLinksOnlyMode" class="col-span-2 grid grid-cols-1 gap-5 md:grid-cols-2">

                <div>
                    <Label class="mb-1.5 block">Content Type</Label>
                    <Tooltip
                        v-if="selectedPlatformIds.includes('instagram') && !isInstagramStoriesEnabled"
                        :show-tooltip="filters.contentType === 'story'"
                        class="w-full"
                    >
                        <template #trigger>
                            <select
                                v-model="filters.contentType"
                                class="h-10 w-full rounded border border-slate-200 px-3.5 py-1 text-sm leading-none"
                                @change="(e) => { if (e.target.value === 'story' && !isInstagramStoriesEnabled) { filters.contentType = 'post' } }"
                            >
                                <option v-for="type in availableContentTypes" :key="type.id === null || type.id === '' ? 'any' : type.id" :value="type.id === null ? '' : type.id" :disabled="type.id === 'story' && !isInstagramStoriesEnabled">
                                    {{ type.label }}{{ type.id === 'story' && !isInstagramStoriesEnabled ? ' (Upgrade Required)' : '' }}
                                </option>
                            </select>
                        </template>
                        <template #default>
                            <div class="whitespace-nowrap text-xs">
                                Upgrade required to access <span class="font-semibold text-white">Instagram Stories</span>
                            </div>
                        </template>
                    </Tooltip>
                    <select
                        v-else
                        v-model="filters.contentType"
                        class="h-10 w-full rounded border border-slate-200 px-3.5 py-1 text-sm leading-none"
                    >
                        <option v-for="type in availableContentTypes" :key="type.id === null || type.id === '' ? 'any' : type.id" :value="type.id === null ? '' : type.id">
                            {{ type.label }}
                        </option>
                    </select>
                    </div>

                    <div>
                        <template v-if="isListsEnabled">
                            <Label class="mb-1.5 block">List Source</Label>
                            <DropdownSource
                                ref="listSources"
                                :platform-id="selectedPlatformIds.length === 1 ? selectedPlatformIds[0] : null"
                                :lists-by-platform-id="listsByPlatformId"
                                :includePlatform="false"
                                :includeCampaigns="false"
                                :showNoSourceSelected="true"
                                noSourceSelectedMessage="No list selected"
                                :initialSelectedSource="initialSelectedList"
                                @change="onListSourceChange"
                            />
                        </template>
                    </div>
                </div>

                <!-- Additional Content - own row, 1 col in 2-col -->
                <div class="col-span-2 grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                        <Label class="mb-1.5 block">{{ additionalContentLabel }}</Label>
                        <div class="flex items-center gap-3">
                            <Button variant="outline" @click="onOpenModal">
                                <AppIcon name="plus" class="mr-1.5 h-3 w-3" />
                                {{ additionalContentButtonLabel }}
                            </Button>
                            <span v-if="hasAdditionalLinks" class="text-sm text-slate-500">
                                {{ filters.additionalContent.length }} link{{ filters.additionalContent.length !== 1 ? 's' : '' }} added
                            </span>
                        </div>
                        <p class="text-xs mt-1">{{ additionalContentDescription }}</p>
                        <div
                            v-if="isLinksOnlyMode && detectedAdditionalPlatformLabels.length > 0"
                            class="mt-3 flex flex-wrap gap-2"
                        >
                            <span
                                v-for="platformLabel in detectedAdditionalPlatformLabels"
                                :key="platformLabel"
                                class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600"
                            >
                                {{ platformLabel }}
                            </span>
                        </div>
                    </div>
                </div>

                <div v-if="sponsorshipConfig" class="col-span-2">
                    <Label class="mb-2 block">Sponsorship Options</Label>
                    <div class="space-y-2">
                        <label class="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                                :checked="sponsorshipConfig.enabled"
                                @update:checked="(val) => { sponsorshipConfig.enabled = val; onChangeSponsorshipConfig() }"
                            />
                            <span class="text-sm">Detect sponsorships</span>
                        </label>
                        <label v-if="sponsorshipConfig.enabled && filters.game" class="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                                :checked="sponsorshipConfig.mustMatchGame"
                                @update:checked="(val) => { sponsorshipConfig.mustMatchGame = val; onChangeSponsorshipConfig() }"
                            />
                            <span class="text-sm">Sponsorships for {{ filters.game.name }} only</span>
                        </label>
                        <label v-if="sponsorshipConfig.enabled" class="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                                :checked="sponsorshipConfig.includeShoutouts"
                                @update:checked="(val) => { sponsorshipConfig.includeShoutouts = val; onChangeSponsorshipConfig() }"
                            />
                            <span class="text-sm">Include shoutouts</span>
                        </label>
                        <p v-if="sponsorshipConfig.enabled" class="text-xs mt-1">Includes content where a sponsorship or ad promotion is only disclosed in the video/post description, rather than in the main title or during a dedicated ad segment.</p>
                    </div>
                </div>

                <div v-if="!isLinksOnlyMode && selectedPlatformIds.includes('twitch') && !filters.listId" class="col-span-2">
                    <Label class="mb-2 block">Content Scope</Label>
                    <label class="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                            :checked="filters.isIgnoreTiny"
                            @update:checked="(val) => { filters.isIgnoreTiny = val }"
                        />
                        <span class="text-sm">Exclude very small creators</span>
                    </label>
                    <p class="text-xs mt-1">Hides streamers averaging under 15 viewers. Including them may slow report generation.</p>
                </div>

            </div>
        </div>

        <div class="flex flex-shrink-0 items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
            <div class="flex items-center text-sm text-slate-500">
                <AppIcon
                    v-if="props.isLoading === true"
                    name="spinner-third"
                    class="mr-2 h-3.5 w-3.5 animate-spin"
                />
                {{ footerMessage }}
            </div>

            <div class="flex flex-shrink-0 items-center gap-2">
                <Button
                    variant="ghost"
                    :disabled="filtersHaveChanged !== true"
                    @click="onResetOptions()"
                >
                    <AppIcon name="rotate-left" class="mr-1.5 h-3.5 w-3.5" />
                    Reset Setup
                </Button>
                <Button
                    :disabled="!canGenerateReport"
                    @click="onGenerateReport()"
                >
                    <AppIcon
                        v-if="props.isLoading === true"
                        name="spinner-third"
                        class="mr-1.5 h-3.5 w-3.5 animate-spin"
                    />
                    <span v-if="editReportId">Update Report</span>
                    <span v-else-if="defaultReportOptions">Copy Report</span>
                    <span v-else>Generate Report</span>
                </Button>
            </div>
        </div>
    </div>
</template>
<style lang="scss">
.flatpickr-wrapper {
    display: block!important;
}
</style>
